import React from 'react';
import {
  EMPTY_FILTERS,
  QueueFilters,
  QueueSort,
  UNASSIGNED,
  activeFilterCount
} from '../../shared/ticket-queue.util';

const FIELD = 'rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-neutral-500';
const LABEL = 'flex flex-col gap-1 text-xs font-gilroy-medium text-neutral-400';

interface Props {
  filters: QueueFilters;
  sort: QueueSort;
  options: { teams: string[]; members: string[]; categories: string[] };
  onChange: (next: QueueFilters) => void;
  onSort: (sort: QueueSort) => void;
}

/** The filter bar over the ticket list (CTO, 2026-10-13). Narrows the queue on screen. */
export const QueueFiltersBar: React.FC<Props> = ({ filters, sort, options, onChange, onSort }) => {
  const set = <K extends keyof QueueFilters>(key: K, value: QueueFilters[K]) =>
    onChange({ ...filters, [key]: value });
  const count = activeFilterCount(filters);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
      <label className={`${LABEL} col-span-2`}>
        Search
        <input
          className={FIELD}
          placeholder="Number, title, team, reservation"
          value={filters.text}
          onChange={(e) => set('text', e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Team
        <select
          className={FIELD}
          value={filters.team}
          onChange={(e) => set('team', e.target.value)}
        >
          <option value="">Any</option>
          {options.teams.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </label>
      <label className={LABEL}>
        Team member
        <select
          className={FIELD}
          value={filters.member}
          onChange={(e) => set('member', e.target.value)}
        >
          <option value="">Anyone</option>
          <option value={UNASSIGNED}>Unassigned</option>
          {options.members.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
      </label>
      <label className={LABEL}>
        Priority
        <select
          className={FIELD}
          value={filters.priority}
          onChange={(e) => set('priority', e.target.value)}
        >
          <option value="">Any</option>
          {['P1', 'P2', 'P3', 'P4'].map((p) => (
            <option key={p}>{p}</option>
          ))}
        </select>
      </label>
      <label className={LABEL}>
        State
        <select
          className={FIELD}
          value={filters.state}
          onChange={(e) => set('state', e.target.value as QueueFilters['state'])}
        >
          <option value="">Any</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>
      </label>
      <label className={LABEL}>
        Category
        <select
          className={FIELD}
          value={filters.category}
          onChange={(e) => set('category', e.target.value)}
        >
          <option value="">Any</option>
          {options.categories.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </label>
      <label className={LABEL}>
        SLA
        <select
          className={FIELD}
          value={filters.sla}
          onChange={(e) => set('sla', e.target.value as QueueFilters['sla'])}
        >
          <option value="">Any</option>
          <option value="breached">Breached</option>
          <option value="at_risk">Due soon</option>
          <option value="on_track">On track</option>
          <option value="met">Met</option>
          <option value="none">No SLA</option>
        </select>
      </label>
      <label className={LABEL}>
        Sort by
        <select
          className={FIELD}
          value={sort}
          onChange={(e) => onSort(e.target.value as QueueSort)}
        >
          <option value="updated">Last updated</option>
          <option value="created">Newest</option>
          <option value="priority">Priority</option>
          <option value="due">SLA due first</option>
        </select>
      </label>
      <label className={LABEL}>
        Created from
        <input
          type="date"
          className={FIELD}
          value={filters.createdFrom}
          onChange={(e) => set('createdFrom', e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Created to
        <input
          type="date"
          className={FIELD}
          value={filters.createdTo}
          onChange={(e) => set('createdTo', e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Updated from
        <input
          type="date"
          className={FIELD}
          value={filters.updatedFrom}
          onChange={(e) => set('updatedFrom', e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Updated to
        <input
          type="date"
          className={FIELD}
          value={filters.updatedTo}
          onChange={(e) => set('updatedTo', e.target.value)}
        />
      </label>
      <label className="flex items-end gap-2 pb-2 text-sm text-neutral-400">
        <input
          type="checkbox"
          checked={filters.emergencyOnly}
          onChange={(e) => set('emergencyOnly', e.target.checked)}
        />
        Emergencies only
      </label>
      <div className="flex items-end pb-1">
        {count || filters.text ? (
          <button
            type="button"
            className="text-sm font-gilroy-medium text-primary-700 hover:underline"
            onClick={() => onChange(EMPTY_FILTERS)}
          >
            Clear filters{count ? ` (${count})` : ''}
          </button>
        ) : null}
      </div>
    </div>
  );
};
